import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { AUDIT_TRAIL_PARAMS_NAMES } from "../../../modules/constants";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import AuditTrailOutlineButton from "../../ui-library/Buttons/AuditTrailOutlineButton/AuditTrailOutlineButton";
import FilterTag from "../../ui-library/FilterTag/FilterTag";
import ActorFilterForm from "../AuditTrailEventsTable/ActorFilterForm/ActorFilterForm";
import SubjectAccountFilterForm from "../AuditTrailEventsTable/SubjectAccountFilterForm/SubjectAccountFilterForm";
import ResourceFilterForm from "../AuditTrailEventsTable/ResourceFilterForm/ResourceFilterForm";
import DateTimeFilterForm from "../AuditTrailEventsTable/DateTimeFilterForm/DateTimeFilterForm";
import CustomDropdownLabels from "../../ui-library/CustomDropdown/CustomDropdownLabels";
import moment from "moment";
import { appStudy } from "../../../config";
import LoadingSpinner from "../../ui-library/LoadingSpinner/LoadingSpinner";

const Container = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-start;
  & > * {
    margin-bottom: 16px;
  }

  & > *:not(:last-child) {
    margin-right: 16px;
  }
`;

const ActionContainer = styled.div`
  @media print {
    visibility: hidden;
  }
  position: absolute;
  top: -70px;
  right: 0;
`;

const StyledButtons = styled.div`
  display: ${(props) => (!props.hideForPrint ? "block" : "none")};
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-end;
  margin-top: 16px;

  & > * {
    margin-bottom: 16px;
  }

  & > *:not(:last-child) {
    margin-right: 16px;
  }
`;

const FilterTagsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
`;

const PrintingLoadingSpinnerContainer = styled.div`
  text-align: center;
`;

const AuditTrailFilters = (props) => {
  const {
    actorTypes = [],
    actions = [],
    statuses = [],
    sites,
    applyFilters,
    filterTags,
    removeFilterTag,
    user_sites,
    onChangeActorType,
    onChangeAction,
    onChangeStatus,
    onChangeSite,
    applyActorFilter,
    applySubjectAccountFilter,
    applyResourceFilter,
    onChangeBeforeAfterDateTimes,
    toggleEventsData,
  } = props;

  const [filterActorFormTarget, setFilterActorFormTarget] = useState(null);
  const [filterSubjectAccountFormTarget, setFilterSubjectAccountFormTarget] =
    useState(null);
  const [filterResourceFormTarget, setFilterResourceFormTarget] =
    useState(null);
  const [filterBeforeFormTarget, setFilterBeforeFormTarget] = useState(null);
  const [filterAfterFormTarget, setFilterAfterFormTarget] = useState(null);

  const [printing, setPrinting] = useState(false);

  const dropdownToggleActorTypeRef = useRef(null);
  const dropdownToggleSubjectSiteRef = useRef(null);
  const dropdownToggleActionRef = useRef(null);
  const dropdownToggleStatusRef = useRef(null);

  const getQueryParams = () => {
    const location = window.location;
    return new URLSearchParams(location.search);
  };

  useEffect(() => {
    if (
      actorTypes &&
      actorTypes.length > 0 &&
      statuses &&
      statuses.length > 0 &&
      actions &&
      actions.length > 0
    ) {
      const filters = {
        filterBefore: null,
        filterAfter: null,
        filterActor: null,
        filterActorType: null,
        filterSubjectAccount: null,
        filterAction: null,
        filterStatus: null,
        filterSubjectSite: null,
        filterResource: null,
      };

      const queryParams = getQueryParams();

      if (queryParams.has(AUDIT_TRAIL_PARAMS_NAMES.before)) {
        filters.filterBefore = new Date(
          queryParams.get(AUDIT_TRAIL_PARAMS_NAMES.before)
        );
      }
      if (queryParams.has(AUDIT_TRAIL_PARAMS_NAMES.after)) {
        filters.filterAfter = new Date(
          queryParams.get(AUDIT_TRAIL_PARAMS_NAMES.after)
        );
      }
      if (queryParams.has(AUDIT_TRAIL_PARAMS_NAMES.actor)) {
        filters.filterActor = queryParams.get(AUDIT_TRAIL_PARAMS_NAMES.actor);
      }
      if (queryParams.has(AUDIT_TRAIL_PARAMS_NAMES.actorType)) {
        filters.filterActorType =
          actorTypes.find(
            (actorType) =>
              actorType.value ===
              queryParams.get(AUDIT_TRAIL_PARAMS_NAMES.actorType)
          )?.value || queryParams.get(AUDIT_TRAIL_PARAMS_NAMES.actorType);
      }
      if (queryParams.has(AUDIT_TRAIL_PARAMS_NAMES.subjectAccount)) {
        filters.filterSubjectAccount = queryParams.get(
          AUDIT_TRAIL_PARAMS_NAMES.subjectAccount
        );
      }
      if (queryParams.has(AUDIT_TRAIL_PARAMS_NAMES.action)) {
        filters.filterAction =
          actions.find(
            (action) =>
              action.value === queryParams.get(AUDIT_TRAIL_PARAMS_NAMES.action)
          )?.value || queryParams.get(AUDIT_TRAIL_PARAMS_NAMES.action);
      }
      if (queryParams.has(AUDIT_TRAIL_PARAMS_NAMES.status)) {
        filters.filterStatus =
          statuses.find(
            (status) =>
              status.value === queryParams.get(AUDIT_TRAIL_PARAMS_NAMES.status)
          )?.value || queryParams.get(AUDIT_TRAIL_PARAMS_NAMES.status);
      }
      if (queryParams.has(AUDIT_TRAIL_PARAMS_NAMES.subjectSite)) {
        filters.filterSubjectSite = queryParams.get(
          AUDIT_TRAIL_PARAMS_NAMES.subjectSite
        );
      }
      if (queryParams.has(AUDIT_TRAIL_PARAMS_NAMES.resource)) {
        filters.filterResource = queryParams.get(
          AUDIT_TRAIL_PARAMS_NAMES.resource
        );
      }

      applyFilters(filters);
    }
  }, [actorTypes, actions, statuses]);

  const onResetFilters = () => {
    applyFilters({
      filterBefore: null,
      filterAfter: null,
      filterActor: null,
      filterActorType: null,
      filterSubjectAccount: null,
      filterAction: null,
      filterStatus: null,
      filterSubjectSite: null,
      filterResource: null,
    });

    window.history.pushState(null, "Rarify", `/audittrail`);
  };

  const onAfterPrint = (event) => {
    document.title = "Rarify";

    // we need to give a timeout to avoid block the dom,
    // because we need to close all the events extra data after print
    setTimeout(() => {
      toggleEventsData(false);

      setPrinting(false);
    }, 500);
  };

  useEffect(() => {
    window.addEventListener("afterprint", onAfterPrint);

    return () => {
      window.removeEventListener("afterprint", onAfterPrint);
    };
  }, []);

  const onPrint = () => {
    setPrinting(true);

    // open the events data for print purposes
    toggleEventsData(true);

    document.title = `Rarify_Audit_${appStudy}_${moment().format(
      "YYYY-MM-DD_HH-mm-SS"
    )}`;

    // we need to give a timeout to avoid block the dom
    // and open the events to show more data
    setTimeout(() => {
      window.print();
    }, 1000);
  };

  const onSubmitActorFilterForm = (filterActor) => {
    applyActorFilter(filterActor);
    setFilterActorFormTarget(null);
  };

  const onSubmitSubjectAccountFilterForm = (filterSubjectAccount) => {
    applySubjectAccountFilter(filterSubjectAccount);
    setFilterSubjectAccountFormTarget(null);
  };

  const onSubmitResourceFilterForm = (filterResource) => {
    applyResourceFilter(filterResource);
    setFilterResourceFormTarget(null);
  };

  const onSubmitAfterFilterForm = (filterBefore, filterAfter) => {
    onChangeBeforeAfterDateTimes(filterBefore, filterAfter);
    setFilterAfterFormTarget(null);
  };

  const onSubmitBeforeFilterForm = (filterBefore, filterAfter) => {
    onChangeBeforeAfterDateTimes(filterBefore, filterAfter);
    setFilterBeforeFormTarget(null);
  };

  const handleOnClickFilterTag = (filterTag, target) => {
    switch (filterTag.id) {
      case AUDIT_TRAIL_PARAMS_NAMES.after:
        setFilterAfterFormTarget(target);
        break;
      case AUDIT_TRAIL_PARAMS_NAMES.before:
        setFilterBeforeFormTarget(target);
        break;
      case AUDIT_TRAIL_PARAMS_NAMES.actor:
        setFilterActorFormTarget(target);
        break;
      case AUDIT_TRAIL_PARAMS_NAMES.actorType:
        dropdownToggleActorTypeRef.current?.click();
        dropdownToggleActorTypeRef.current?.focus();
        break;
      case AUDIT_TRAIL_PARAMS_NAMES.subjectAccount:
        setFilterSubjectAccountFormTarget(target);
        break;
      case AUDIT_TRAIL_PARAMS_NAMES.subjectSite:
        dropdownToggleSubjectSiteRef.current?.click();
        dropdownToggleSubjectSiteRef.current?.focus();
        break;
      case AUDIT_TRAIL_PARAMS_NAMES.action:
        dropdownToggleActionRef.current?.click();
        dropdownToggleActionRef.current?.focus();
        break;
      case AUDIT_TRAIL_PARAMS_NAMES.status:
        dropdownToggleStatusRef.current?.click();
        dropdownToggleStatusRef.current?.focus();
        break;
      case AUDIT_TRAIL_PARAMS_NAMES.resource:
        setFilterResourceFormTarget(target);
        break;
      default:
        break;
    }
  };

  const filteredFilterTags = useMemo(
    () => Object.values(filterTags)?.filter((filterTag) => filterTag !== null),
    [filterTags]
  );

  return (
    <>
      {filteredFilterTags && filteredFilterTags.length > 0 && (
        <Container data-testid="audit-trail-filters">
          <FilterTagsContainer data-testid="filter-tags-container">
            {filteredFilterTags.map((filterTag, index) => (
              <div key={filterTag.id}>
                {filterTag.id === AUDIT_TRAIL_PARAMS_NAMES.after && (
                  <DateTimeFilterForm
                    index={index}
                    show={!!filterAfterFormTarget}
                    onClose={() => setFilterAfterFormTarget(null)}
                    currentFilterAfter={
                      filterTags.filterAfter?.value
                        ? new Date(filterTags.filterAfter.value)
                        : null
                    }
                    currentFilterBefore={
                      filterTags.filterBefore?.value
                        ? new Date(filterTags.filterBefore.value)
                        : null
                    }
                    onSubmit={onSubmitAfterFilterForm}
                    target={filterAfterFormTarget}
                  />
                )}
                {filterTag.id === AUDIT_TRAIL_PARAMS_NAMES.before && (
                  <DateTimeFilterForm
                    index={index}
                    show={!!filterBeforeFormTarget}
                    onClose={() => setFilterBeforeFormTarget(null)}
                    currentFilterAfter={
                      filterTags.filterAfter?.value
                        ? new Date(filterTags.filterAfter.value)
                        : null
                    }
                    currentFilterBefore={
                      filterTags.filterBefore?.value
                        ? new Date(filterTags.filterBefore.value)
                        : null
                    }
                    onSubmit={onSubmitBeforeFilterForm}
                    target={filterBeforeFormTarget}
                  />
                )}

                {filterTag.id === AUDIT_TRAIL_PARAMS_NAMES.actor && (
                  <ActorFilterForm
                    show={!!filterActorFormTarget}
                    onClose={() => setFilterActorFormTarget(null)}
                    currentFilterActor={filterTags.filterActor?.value}
                    onSubmit={onSubmitActorFilterForm}
                    target={filterActorFormTarget}
                  />
                )}

                {filterTag.id === AUDIT_TRAIL_PARAMS_NAMES.subjectAccount && (
                  <SubjectAccountFilterForm
                    show={!!filterSubjectAccountFormTarget}
                    onClose={() => setFilterSubjectAccountFormTarget(null)}
                    currentFilterSubjectAccount={
                      filterTags.filterSubjectAccount?.value
                    }
                    onSubmit={onSubmitSubjectAccountFilterForm}
                    target={filterSubjectAccountFormTarget}
                  />
                )}

                {filterTag.id === AUDIT_TRAIL_PARAMS_NAMES.resource && (
                  <ResourceFilterForm
                    show={!!filterResourceFormTarget}
                    onClose={() => setFilterResourceFormTarget(null)}
                    currentFilterResource={filterTags.filterResource?.value}
                    onSubmit={onSubmitResourceFilterForm}
                    target={filterResourceFormTarget}
                  />
                )}

                {filterTag.id === AUDIT_TRAIL_PARAMS_NAMES.actorType && (
                  <CustomDropdownLabels
                    className="noprint"
                    values={actorTypes}
                    limitheight={true}
                    onChange={(e) => onChangeActorType(e.value)}
                    data-testid="atf-actor-types"
                    size="small"
                    hiddenToggle={true}
                    dropdownToggleRef={dropdownToggleActorTypeRef}
                  />
                )}

                {filterTag.id === AUDIT_TRAIL_PARAMS_NAMES.subjectSite &&
                  user_sites?.length === 0 && (
                    <CustomDropdownLabels
                      className="noprint"
                      values={sites}
                      limitheight={true}
                      onChange={(e) => onChangeSite(e.value)}
                      data-testid="atf-sites"
                      size="small"
                      hiddenToggle={true}
                      dropdownToggleRef={dropdownToggleSubjectSiteRef}
                    />
                  )}

                {filterTag.id === AUDIT_TRAIL_PARAMS_NAMES.action && (
                  <CustomDropdownLabels
                    className="noprint"
                    values={actions}
                    limitheight={true}
                    onChange={(e) => onChangeAction(e.value)}
                    data-testid="atf-actions"
                    size="small"
                    hiddenToggle={true}
                    dropdownToggleRef={dropdownToggleActionRef}
                  />
                )}

                {filterTag.id === AUDIT_TRAIL_PARAMS_NAMES.status && (
                  <CustomDropdownLabels
                    className="noprint"
                    values={statuses}
                    limitheight={true}
                    onChange={(e) => onChangeStatus(e.value)}
                    data-testid="atf-statuses"
                    size="small"
                    hiddenToggle={true}
                    dropdownToggleRef={dropdownToggleStatusRef}
                  />
                )}

                <FilterTag
                  key={filterTag.id}
                  data-testid={`filter-tag-${filterTag.id}`}
                  filterTag={filterTag}
                  onRemove={(tag) => removeFilterTag(tag)}
                  onClick={(tag, target) => handleOnClickFilterTag(tag, target)}
                />
              </div>
            ))}
          </FilterTagsContainer>
        </Container>
      )}

      {printing && (
        <PrintingLoadingSpinnerContainer className="noprint">
          <LoadingSpinner />
          <p>Printing...</p>
        </PrintingLoadingSpinnerContainer>
      )}

      <ActionContainer>
        <StyledButtons>
          <AuditTrailOutlineButton
            variant="outline-primary"
            data-testid="reset-filters-button"
            onClick={() => onResetFilters()}
            size="sm"
          >
            Reset Filters
          </AuditTrailOutlineButton>
          <PrimaryButton
            data-testid="print-button"
            size="sm"
            onClick={() => onPrint()}
          >
            Print
          </PrimaryButton>
        </StyledButtons>
      </ActionContainer>
    </>
  );
};

export default AuditTrailFilters;
